/**
 * Cashflows Forecast dashboard — `/dashboard/cashflows`.
 *
 * Convert 1:1 từ page Power BI "Cashflows Forecast" (`PBI_ERP.pbix`, skill
 * /convert-pbi): HEADER (title + tuần hiện tại) + hàng 3 cột (I. GENERAL INFOR:
 * FX + Market LRC / II. FINANCE POSITIONS: Cash + Credit facilities + USD
 * exposure) + matrix III. CASH FLOWS FORECAST full-width (32 dòng template ×
 * 8 tuần). GIAO DIỆN upgrade theo design `plan/report/erp-report-cash.jsx`
 * ("TLC ERP · Management Report"): card trắng viền mảnh radius 8, title ink +
 * caption mono, bảng header forest chữ trắng, số IBM Plex Mono (tokens
 * `reportTokens.ts`, bảng `CashTable`/`ForecastTable` riêng page — data/layout
 * section giữ nguyên PBI).
 *
 * KHÔNG có date picker: page PBI dùng filter RelativeDate cố định
 * (hôm nay .. hôm nay+49 ngày) — BE tự tính window, FE chỉ hiển thị.
 * DATA: Report DB qua `useGetCashflowsDashboardQuery` (GET /api/v1/dashboard/cashflows).
 */
import { Box, Button, CircularProgress, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import dayjs from 'dayjs'

import { useGetCashflowsDashboardQuery } from '../../../store/api/dashboardCashflowsApi'
import { ReportPageToolbar } from '../../report/reportToolbarSlot'
import { useReportZoom } from '../_shared/useReportZoom'
import type { PnLColumn, PnLRow } from '../pnl/PnLTable'
import CashTable from './CashTable'
import ForecastTable from './ForecastTable'
import { FONT_MONO, FONT_SANS, RPT } from './reportTokens'
import {
  cashColumns,
  creditColumns,
  exposureColumns,
  fxColumns,
  marketColumns,
} from './cashflowsColumns'

/** Card trắng theo design Card (erp-report-cash): viền mảnh, radius 8, title
 *  ink đậm + caption mono bên phải (ngày snapshot — thay 2 card
 *  `lastest_date`/`lastest_timing` nổi của PBI). */
function CashflowCard({
  title,
  caption,
  columns,
  rows,
  loading,
  children,
  height,
  fill,
  sx,
}: {
  title?: string
  caption?: string
  columns?: PnLColumn[]
  rows?: PnLRow[]
  loading?: boolean
  children?: React.ReactNode
  /** Height bảng cố định (px) — bảng dài scroll dọc + sticky header. */
  height?: number
  /** Bảng ngắn stretch khít height card (grid stretch) — phần dư chia đều
   *  height từng dòng, không hở đáy (CashTable `fill`). */
  fill?: boolean
  /** Vị trí trong grid section (gridColumn/gridRow) — card là grid item trực tiếp. */
  sx?: object
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1,
        border: `1px solid ${RPT.border}`,
        borderRadius: '8px',
        bgcolor: '#fff',
        minWidth: 0,
        // Flex column để vùng bảng (`fill`) ăn hết phần height card được grid stretch.
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      {(title || caption) && (
        <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mb: 0.75 }}>
          {title ? (
            <Typography
              sx={{ fontFamily: FONT_SANS, fontSize: '0.78rem', fontWeight: 700, color: RPT.ink, letterSpacing: '0.01em' }}
            >
              {title}
            </Typography>
          ) : null}
          <Box sx={{ flex: 1 }} />
          {caption ? (
            <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.66rem', color: RPT.gold, fontWeight: 600 }}>
              {caption}
            </Typography>
          ) : null}
        </Stack>
      )}
      <Box sx={{ height: 2, mb: 0.25 }}>{loading && <LinearProgress sx={{ height: 2, borderRadius: 1 }} />}</Box>
      <Box
        sx={{
          opacity: loading ? 0.55 : 1,
          pointerEvents: loading ? 'none' : 'auto',
          transition: 'opacity 0.15s',
          ...(fill ? { flex: 1, minHeight: 0 } : {}),
        }}
      >
        {columns ? <CashTable columns={columns} rows={rows ?? []} height={height} fill={fill} /> : children}
      </Box>
    </Paper>
  )
}

/** Format ngày snapshot `DD-MMM-YYYY` (chuẩn date toàn app — Access parity;
 *  deviation khỏi dd/mm/yyyy của PBI, chị Đẹp yêu cầu 2026-06-10). */
const fmtSnapshotDate = (iso?: string | null) => (iso ? dayjs(iso).format('DD-MMM-YYYY') : '')

export default function CashflowsDashboardPage() {
  const { data, isFetching, refetch } = useGetCashflowsDashboardQuery()
  const { zoom, control } = useReportZoom()

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pb: 1 }}>
      {/* ── HEADER: title PBI (textbox 28pt) + card tuần hiện tại + window. ──
          Portal NGUYÊN block lên block chung của ReportsPage khi nhúng. */}
      <ReportPageToolbar>
      <Paper
        elevation={0}
        sx={{
          px: { xs: 1, sm: 1.5 },
          py: 1,
          border: `1px solid ${RPT.border}`,
          borderRadius: '8px',
          bgcolor: '#fff',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {/* Title + info 1 dòng (date/info nằm KẾ BÊN title) — đồng bộ format header các page khác. */}
        <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: FONT_SANS,
              fontSize: { xs: '0.92rem', sm: '1rem' },
              fontWeight: 900,
              color: RPT.forest,
              letterSpacing: '0.01em',
              lineHeight: 1.1,
            }}
          >
            FINANCE POSITIONS & CASH FLOWS FORECAST
          </Typography>
          {data?.currentWoy ? (
            <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.74rem', color: RPT.sub, fontWeight: 600 }}>
              Current week: {data.currentWoy}
              {data.windowTo ? ` · Forecast to ${dayjs(data.windowTo).format('DD MMM YYYY')}` : ''}
            </Typography>
          ) : null}
        </Stack>
        <Box sx={{ flex: 1, minWidth: 0 }} />
        {control}
        {/* Reload toàn dashboard — refetch() ép gọi lại kể cả khi cache còn (mirror PnL page). */}
        <Tooltip title="Reload dashboard data">
          <span>
            <Button
              size="small"
              variant="outlined"
              onClick={() => refetch()}
              disabled={isFetching}
              startIcon={isFetching ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', py: 0.25, px: 1, minHeight: 28, fontSize: '0.75rem' }}
            >
              Reload
            </Button>
          </span>
        </Tooltip>
      </Paper>
      </ReportPageToolbar>

      {/* Zoom report áp lên TOÀN bộ nội dung (grid + forecast) — 1 container. */}
      <Box sx={{ zoom, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* ── HÀNG TRÊN — 1 GRID chung 3 cột × 2 hàng (PBI w403 : 537 : 432 →
          1 : 1.33 : 1.07): card đặt explicit gridColumn/gridRow để THẲNG HÀNG
          theo band như PBI — hàng 1: FX | Cash | Exposure (exposure cao span
          cả 2 hàng), hàng 2: Market | Credit. Card cùng hàng stretch bằng
          chiều cao nhau (grid default). Mobile: stack theo thứ tự section. ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) minmax(0,1.33fr) minmax(0,1.07fr)' },
          gap: 1,
        }}
      >
        <CashflowCard
          title="I. GENERAL INFOR"
          caption={fmtSnapshotDate(data?.fxDate)}
          columns={fxColumns}
          rows={data?.fxRates}
          loading={isFetching}
          sx={{ gridColumn: { lg: '1' }, gridRow: { lg: '1' } }}
        />
        <CashflowCard
          title="Market Price"
          caption={fmtSnapshotDate(data?.marketDate)}
          columns={marketColumns}
          rows={data?.marketPrices}
          height={110}
          loading={isFetching}
          sx={{ gridColumn: { lg: '1' }, gridRow: { lg: '2' } }}
        />
        <CashflowCard
          title="II. FINANCE POSITIONS"
          columns={cashColumns}
          rows={data?.cashAtBanks}
          loading={isFetching}
          sx={{ gridColumn: { lg: '2' }, gridRow: { lg: '1' } }}
        />
        <CashflowCard
          title="Credit Facilities"
          columns={creditColumns}
          rows={data?.creditFacilities}
          fill
          loading={isFetching}
          sx={{ gridColumn: { lg: '2' }, gridRow: { lg: '2' } }}
        />
        <CashflowCard
          title="USD Exposure"
          columns={exposureColumns}
          rows={data?.usdExposure}
          fill
          loading={isFetching}
          sx={{ gridColumn: { lg: '3' }, gridRow: { lg: '1 / span 2' } }}
        />
      </Box>

      {/* ── III. CASH FLOWS FORECAST — matrix full-width (PBI Group 1). ── */}
      <CashflowCard title="III. CASH FLOWS FORECAST" loading={isFetching}>
        <ForecastTable weeks={data?.weeks ?? []} rows={data?.forecast ?? []} />
      </CashflowCard>
      </Box>
    </Box>
  )
}
